import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { useBankNameLookup } from 'hooks/useBankNameLookup'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { submitWireInstructionTemplate } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { wireInstructionTemplateInputs } from './inputs'
import type { IWireInstructionTemplate } from './types'

export function WireInstructionTemplateDetails(props: {
  defaults: IWireInstructionTemplate | null
  lastUpdatedAt: number
  onBack: Function
  onComplete: Function
}) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const { fillBankNameInput } = useBankNameLookup()
  const { defaults = null, lastUpdatedAt } = props

  useEffect(() => {
    if (!defaults) {
      setInputs(wireInstructionTemplateInputs())
      return
    }
    const newStats = cloneDeep(wireInstructionTemplateInputs()) as Record<string, any>
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    setInputs(newStats)
  }, [defaults, lastUpdatedAt])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
    if (key === 'bankRoutingNumber') fillBankNameInput(newInputs, key, 'bankName', setInputs)
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      const error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }

    if (hasError) {
      setInputs(newStats)
      return
    }

    data.title = defaults?.title ?? `Wire instruction template ${Date.now()}`

    setLoading(true)
    const id = defaults ? defaults.id : 0
    submitWireInstructionTemplate(id, data)
      .then(() => {
        if (id == 0) toast('Wire instruction template is added.', { type: 'success' })
        else toast('Wire instruction template is updated.', { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  return (
    <div className="wire-instruction-template-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Wire Instruction Templates — {!defaults ? 'Add' : 'Update'}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" alt="" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Wire Instruction Templates</p>
        </div>
      </Button>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-4">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          return (
            <div className={`input col-span-${input.span || 1}`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'} Template</>
        </Button>
      </div>
    </div>
  )
}
